import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, RotateCw, Target, Save, Plus, X, Eye, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductEditorPage() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sirvSpinUrl, setSirvSpinUrl] = useState('');
  const [hotspots, setHotspots] = useState([]);
  const [newHotspot, setNewHotspot] = useState({
    tooltip_content: '',
    box_content: '',
    frame_number: 1,
    position_x: '50%',
    position_y: '50%',
    style: 't-ball'
  });
  const [previewMode, setPreviewMode] = useState(false);

  const storeId = localStorage.getItem('store_id');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!storeId) {
      navigate('/');
      return;
    }

    fetchProduct();
  }, [storeId, productId, navigate]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${productId}`, {
        headers: {
          'X-Store-Id': storeId
        }
      });
      
      const data = response.data;
      setProduct(data);
      setSirvSpinUrl(data.sirv_spin_url || '');
      setHotspots(data.hotspots || []);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Error al cargar producto');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      // Upload files to Sirv
      const uploadPromises = files.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('filename', `products/${productId}/frame_${index + 1}.${file.name.split('.').pop()}`);

        return axios.post(`${API}/sirv/upload`, formData, {
          headers: {
            'X-Store-Id': storeId,
            'Content-Type': 'multipart/form-data'
          }
        });
      });

      const results = await Promise.all(uploadPromises);
      
      if (results.length > 0) {
        // For simplicity, use the first uploaded file as the spin URL
        // In a real scenario, you'd create a .spin file with all frames
        const firstUrl = results[0].data.url;
        setSirvSpinUrl(firstUrl);
        toast.success(`${files.length} archivo(s) subido(s) correctamente`);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Error al subir archivos');
    } finally {
      setUploading(false);
    }
  };

  const addHotspot = () => {
    if (!newHotspot.tooltip_content) {
      toast.error('Por favor ingresa el contenido del tooltip');
      return;
    }

    const hotspot = {
      ...newHotspot,
      id: Date.now().toString()
    };

    setHotspots([...hotspots, hotspot]);
    
    // Reset form
    setNewHotspot({
      tooltip_content: '',
      box_content: '',
      frame_number: 1,
      position_x: '50%',
      position_y: '50%',
      style: 't-ball'
    });
    
    toast.success('Hotspot añadido');
  };

  const removeHotspot = (id) => {
    setHotspots(hotspots.filter(h => h.id !== id));
    toast.success('Hotspot eliminado');
  };

  const handleSave = async () => {
    if (!sirvSpinUrl) {
      toast.error('Por favor sube al menos una imagen');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/products/${productId}`,
        {
          sirv_spin_url: sirvSpinUrl,
          hotspots: hotspots,
          status: 'configured'
        },
        {
          headers: {
            'X-Store-Id': storeId
          }
        }
      );

      toast.success('Configuración guardada correctamente');
      
      // Refresh product data
      await fetchProduct();
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const loadSirvScript = () => {
    if (document.getElementById('sirv-script')) return;

    const script = document.createElement('script');
    script.id = 'sirv-script';
    script.src = 'https://scripts.sirv.com/sirvjs/v3/sirv.js';
    script.async = true;
    document.body.appendChild(script);
  };

  useEffect(() => {
    if (sirvSpinUrl && previewMode) {
      loadSirvScript();
    }
  }, [sirvSpinUrl, previewMode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-editor-loading">
        <RotateCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="product-editor-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="gap-2"
              data-testid="back-to-dashboard-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Dashboard
            </Button>
            <div className="flex gap-3">
              <Button
                onClick={() => setPreviewMode(!previewMode)}
                variant="outline"
                className="gap-2"
                data-testid="preview-toggle-button"
              >
                <Eye className="w-4 h-4" />
                {previewMode ? 'Editar' : 'Vista Previa'}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !sirvSpinUrl}
                className="gap-2"
                data-testid="save-config-button"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Guardar Configuración
                  </>
                )}
              </Button>
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">{product?.product_name || 'Producto'}</h1>
            <p className="text-zinc-600">Configura el spin 360° y hotspots</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {previewMode && sirvSpinUrl ? (
          /* Preview Mode */
          <div className="bg-white rounded-xl border border-zinc-200 p-8 shadow-sm" data-testid="preview-container">
            <h2 className="text-xl font-bold text-zinc-900 mb-6">Vista Previa</h2>
            <div className="aspect-square bg-zinc-100 rounded-lg overflow-hidden flex items-center justify-center">
              <div className="Sirv" data-src={sirvSpinUrl}>
                <img src={sirvSpinUrl} alt="Product 360" />
              </div>
            </div>
            {hotspots.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-zinc-900 mb-3">Hotspots Configurados</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {hotspots.map((hotspot) => (
                    <div key={hotspot.id} className="bg-zinc-50 rounded-lg p-4 border border-zinc-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className="mb-2">Frame {hotspot.frame_number}</Badge>
                          <p className="text-sm font-medium text-zinc-900">{hotspot.tooltip_content}</p>
                          {hotspot.box_content && (
                            <p className="text-xs text-zinc-600 mt-1">{hotspot.box_content}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Edit Mode */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Upload & Preview */}
            <div className="space-y-6">
              <Card data-testid="upload-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Subir Imágenes 360°
                  </CardTitle>
                  <CardDescription>
                    Sube múltiples imágenes para crear un spin 360° de tu producto
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    variant="outline"
                    className="w-full h-32 border-2 border-dashed border-zinc-300 hover:border-primary transition-colors"
                    data-testid="upload-button"
                  >
                    {uploading ? (
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                        <p>Subiendo...</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                        <p className="text-sm font-medium">Haz clic para subir imágenes</p>
                        <p className="text-xs text-zinc-500 mt-1">o arrastra y suelta</p>
                      </div>
                    )}
                  </Button>

                  {sirvSpinUrl && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-900">Imágenes subidas</p>
                          <p className="text-xs text-green-700 mt-1 break-all">{sirvSpinUrl}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 space-y-2">
                    <Label htmlFor="sirv_url">O ingresa URL de Sirv manualmente</Label>
                    <Input
                      id="sirv_url"
                      type="url"
                      placeholder="https://tu-cuenta.sirv.com/producto.spin"
                      value={sirvSpinUrl}
                      onChange={(e) => setSirvSpinUrl(e.target.value)}
                      data-testid="sirv-url-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Hotspots */}
            <div className="space-y-6">
              <Card data-testid="hotspots-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Configurar Hotspots
                  </CardTitle>
                  <CardDescription>
                    Añade puntos de interés interactivos en tu producto
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="add" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="add" data-testid="add-hotspot-tab">Añadir Hotspot</TabsTrigger>
                      <TabsTrigger value="list" data-testid="list-hotspots-tab">
                        Lista ({hotspots.length})
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="add" className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="tooltip_content">Contenido del Tooltip *</Label>
                        <Input
                          id="tooltip_content"
                          placeholder="Ej: Acabado premium"
                          value={newHotspot.tooltip_content}
                          onChange={(e) => setNewHotspot({...newHotspot, tooltip_content: e.target.value})}
                          data-testid="hotspot-tooltip-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="box_content">Descripción Detallada (Opcional)</Label>
                        <Textarea
                          id="box_content"
                          placeholder="Información adicional que aparecerá al hacer clic"
                          value={newHotspot.box_content}
                          onChange={(e) => setNewHotspot({...newHotspot, box_content: e.target.value})}
                          rows={3}
                          data-testid="hotspot-description-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="frame_number">Número de Frame</Label>
                          <Input
                            id="frame_number"
                            type="number"
                            min="1"
                            value={newHotspot.frame_number}
                            onChange={(e) => setNewHotspot({...newHotspot, frame_number: parseInt(e.target.value) || 1})}
                            data-testid="hotspot-frame-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="style">Estilo</Label>
                          <select
                            id="style"
                            className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                            value={newHotspot.style}
                            onChange={(e) => setNewHotspot({...newHotspot, style: e.target.value})}
                            data-testid="hotspot-style-select"
                          >
                            <option value="t-ball">Bola</option>
                            <option value="t-pin">Pin</option>
                            <option value="t-dot">Punto</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="position_x">Posición X</Label>
                          <Input
                            id="position_x"
                            placeholder="50%"
                            value={newHotspot.position_x}
                            onChange={(e) => setNewHotspot({...newHotspot, position_x: e.target.value})}
                            data-testid="hotspot-x-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="position_y">Posición Y</Label>
                          <Input
                            id="position_y"
                            placeholder="50%"
                            value={newHotspot.position_y}
                            onChange={(e) => setNewHotspot({...newHotspot, position_y: e.target.value})}
                            data-testid="hotspot-y-input"
                          />
                        </div>
                      </div>

                      <Button
                        onClick={addHotspot}
                        className="w-full gap-2"
                        data-testid="add-hotspot-button"
                      >
                        <Plus className="w-4 h-4" />
                        Añadir Hotspot
                      </Button>
                    </TabsContent>

                    <TabsContent value="list" className="space-y-3" data-testid="hotspots-list">
                      {hotspots.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500">
                          <Target className="w-12 h-12 text-zinc-300 mx-auto mb-2" />
                          <p>No hay hotspots configurados</p>
                        </div>
                      ) : (
                        hotspots.map((hotspot) => (
                          <div
                            key={hotspot.id}
                            className="flex items-start justify-between p-4 bg-zinc-50 rounded-lg border border-zinc-200"
                            data-testid={`hotspot-item-${hotspot.id}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline">Frame {hotspot.frame_number}</Badge>
                                <Badge variant="secondary">{hotspot.style}</Badge>
                              </div>
                              <p className="text-sm font-medium text-zinc-900">{hotspot.tooltip_content}</p>
                              {hotspot.box_content && (
                                <p className="text-xs text-zinc-600 mt-1">{hotspot.box_content}</p>
                              )}
                              <p className="text-xs text-zinc-500 mt-2">
                                Posición: {hotspot.position_x}, {hotspot.position_y}
                              </p>
                            </div>
                            <Button
                              onClick={() => removeHotspot(hotspot.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-hotspot-${hotspot.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
